import { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { CheckCircle2, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Promocoes() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'new' | 'already' | null

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast.error(t("promotions.emailError"));
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/api/newsletter/subscribe`, { email: trimmed });
      setStatus(r.data?.already_subscribed ? "already" : "new");
      toast.success(t("promotions.successTitle"));
    } catch (err) {
      const msg = err?.response?.data?.detail || t("promotions.sendError");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = t("promotions.benefits") || [];

  return (
    <div data-testid="promotions-page" className="mx-auto max-w-5xl px-5 pb-24 pt-32 sm:px-8 sm:pt-40">
      <Seo title={`${t("promotions.title")} | Magnolia Lunar`} description={t("promotions.subtitle")} />

      <Reveal>
        <p className="text-[10px] uppercase tracking-[0.45em] text-gold">{t("promotions.label")}</p>
        <h1 className="mt-3 font-display text-4xl font-light text-foreground sm:text-5xl lg:text-6xl">
          {t("promotions.title")}
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground/72">{t("promotions.subtitle")}</p>
      </Reveal>

      {/* Subscribe form */}
      <Reveal delay={0.15}>
        <div className="mt-12 overflow-hidden rounded-2xl border border-gold/20 bg-gradient-to-br from-gold/10 via-transparent to-transparent p-8 sm:p-12">
          {status ? (
            <div data-testid="promotions-success" className="text-center">
              <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.5} />
              <h2 className="mt-5 font-display text-3xl text-foreground">{t("promotions.successTitle")}</h2>
              <p className="mt-3 text-sm text-foreground/72">
                {status === "already" ? t("promotions.alreadyText") : t("promotions.successText")}
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mx-auto max-w-xl" data-testid="promotions-form">
              <label htmlFor="promo-email" className="block">
                <span className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
                  <Mail className="h-4 w-4" strokeWidth={1.5} />
                  {t("promotions.emailPlaceholder")}
                </span>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                  <input
                    id="promo-email"
                    data-testid="promo-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@exemplo.com"
                    className="flex-1 rounded-full border border-foreground/12 bg-background/60 px-6 py-3 text-sm text-foreground outline-none transition-colors focus:border-gold"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="promo-subscribe-button"
                    className="whitespace-nowrap rounded-full bg-gold px-8 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:bg-gold-light disabled:opacity-60"
                  >
                    {submitting ? t("promotions.subscribing") : t("promotions.subscribe")}
                  </button>
                </div>
              </label>
              <p className="mt-4 text-center text-xs text-foreground/50">{t("promotions.privacyNote")}</p>
            </form>
          )}
        </div>
      </Reveal>

      {/* Benefits */}
      <Reveal delay={0.25}>
        <div className="mt-16">
          <h2 className="flex items-center gap-3 font-display text-2xl text-foreground">
            <Sparkles className="h-5 w-5 text-gold" strokeWidth={1.5} />
            {t("promotions.benefitsTitle")}
          </h2>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2">
            {benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 rounded-2xl border border-foreground/8 bg-card p-5 text-sm text-foreground/82">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  );
}
